/**
 * Engineer-tier pod exec / web shell drawer (#249).
 *
 * xterm.js terminal that bridges over a WebSocket to the BFF
 * (`/api/cluster/:siteId/pods/:ns/:pod/exec`), which proxies to
 * deployment-service, which opens a kube exec session.
 *
 * Auth: the WS connection sends the access token via the
 * `Sec-WebSocket-Protocol: bearer.<token>` sub-protocol — browsers can't set
 * Authorization on a WebSocket. The BFF's `authenticateUpgrade` helper
 * extracts and verifies it.
 *
 * Reconnect UX (v1, intentionally simple): on close, render an inline
 * "Disconnected. Reconnect?" panel with a button. Connection state is one
 * of `connecting` / `connected` / `disconnected`. We deliberately do NOT
 * auto-reconnect — re-opening a shell is a privileged action and should be
 * an explicit operator decision (each reconnect emits a new audit event).
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  siteId: string;
  namespace: string;
  podName: string;
  container?: string;
  shell?: string;
  onClose: () => void;
}

type ConnState = 'connecting' | 'connected' | 'disconnected';

export default function PodExecDrawer({
  siteId,
  namespace,
  podName,
  container,
  shell = 'bash',
  onClose,
}: Props) {
  const termRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<ConnState>('connecting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reconnectKey, setReconnectKey] = useState(0);
  const { getAccessToken } = useAuth();

  // Initialize the xterm instance once. The same terminal is reused across
  // reconnect attempts so scrollback isn't lost.
  useEffect(() => {
    if (!termRef.current) return;
    const term = new Terminal({
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 13,
      theme: {
        background: '#0d1117',
        foreground: '#c9d1d9',
        cursor: '#fbbf24',
      },
      cursorBlink: true,
      convertEol: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(termRef.current);
    fit.fit();
    xtermRef.current = term;
    fitRef.current = fit;

    const handleResize = () => {
      try {
        fit.fit();
      } catch {
        // ignore
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
      xtermRef.current = null;
      fitRef.current = null;
    };
  }, []);

  // Connect / reconnect WebSocket.
  useEffect(() => {
    const term = xtermRef.current;
    if (!term) return;

    const token = getAccessToken();
    if (!token) {
      setState('disconnected');
      setErrorMessage('No access token — please re-login.');
      return;
    }

    setState('connecting');
    setErrorMessage(null);

    // Build WS URL: same origin as the page, /api/cluster path, query params
    // for container + shell. Use the page's protocol (wss for https).
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const params = new URLSearchParams();
    if (container) params.set('container', container);
    if (shell) params.set('shell', shell);
    const url = `${proto}//${window.location.host}/api/cluster/${encodeURIComponent(siteId)}/pods/${encodeURIComponent(namespace)}/${encodeURIComponent(podName)}/exec${params.toString() ? `?${params.toString()}` : ''}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url, [`bearer.${token}`]);
    } catch (err) {
      setState('disconnected');
      setErrorMessage((err as Error).message);
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      setState('connected');
      term.writeln(`\x1b[2m[connected to ${namespace}/${podName}${container ? ` (${container})` : ''} via ${shell}]\x1b[0m`);
      try {
        fitRef.current?.fit();
      } catch {
        // ignore
      }
    };

    ws.onmessage = (ev) => {
      const data = ev.data;
      if (typeof data === 'string') {
        term.write(data);
      } else if (data instanceof ArrayBuffer) {
        term.write(new Uint8Array(data));
      } else if (data instanceof Blob) {
        // Defensive: some environments deliver Blob — convert.
        data.arrayBuffer().then((buf) => term.write(new Uint8Array(buf)));
      }
    };

    ws.onerror = () => {
      setErrorMessage('connection error');
    };

    ws.onclose = (ev) => {
      setState('disconnected');
      if (ev.code === 4000 || ev.reason === 'idle') {
        setErrorMessage('Disconnected: idle timeout (15min).');
      } else if (ev.code !== 1000) {
        setErrorMessage(ev.reason || `Disconnected (code ${ev.code}).`);
      }
      term.writeln(`\r\n\x1b[2m[disconnected]\x1b[0m`);
    };

    // Wire stdin from xterm to WS.
    const dataHandler = term.onData((input) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(input);
      }
    });

    return () => {
      dataHandler.dispose();
      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'client');
        }
      } catch {
        // ignore
      }
      wsRef.current = null;
    };
    // reconnectKey is included so clicking Reconnect re-runs the effect.
  }, [siteId, namespace, podName, container, shell, reconnectKey, getAccessToken]);

  function handleReconnect(): void {
    setReconnectKey((k) => k + 1);
  }

  function handleDisconnect(): void {
    const ws = wsRef.current;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      ws.close(1000, 'client');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      data-testid="pod-exec-drawer"
    >
      <div className="bg-surface border border-surface-elevated rounded-xl w-[90vw] max-w-5xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-elevated">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-silver-bright truncate">
              Shell: <span className="text-gold font-mono">{podName}</span>
              {container && <span className="text-silver-muted"> / {container}</span>}
            </h3>
            <p className="text-xs text-silver-muted">
              {namespace} · {shell} ·{' '}
              <span data-testid="pod-exec-state" className={
                state === 'connected' ? 'text-status-healthy' :
                state === 'connecting' ? 'text-status-warning' :
                'text-status-error'
              }>
                {state === 'connected' ? 'Connected' : state === 'connecting' ? 'Connecting…' : 'Disconnected'}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {state === 'connected' && (
              <button
                onClick={handleDisconnect}
                data-testid="pod-exec-disconnect"
                className="px-2.5 py-1.5 text-xs bg-status-warning/10 hover:bg-status-warning/20 text-status-warning rounded-lg transition-colors"
              >
                Disconnect
              </button>
            )}
            {state === 'disconnected' && (
              <button
                onClick={handleReconnect}
                data-testid="pod-exec-reconnect"
                className="px-2.5 py-1.5 text-xs bg-gold/10 hover:bg-gold/20 text-gold rounded-lg transition-colors"
              >
                Reconnect
              </button>
            )}
            <button onClick={onClose} className="text-silver-muted hover:text-silver-bright ml-1">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Disconnect notice */}
        {state === 'disconnected' && errorMessage && (
          <div
            data-testid="pod-exec-error"
            className="px-4 py-2 bg-status-error/5 border-b border-status-error/20 text-xs text-status-error"
          >
            {errorMessage}
          </div>
        )}

        {/* Terminal */}
        <div ref={termRef} data-testid="pod-exec-terminal" className="flex-1 bg-[#0d1117] p-2 overflow-hidden" />
      </div>
    </div>
  );
}
